interface Circle {
    x: number;          // x-coordinate position
    y: number;          // y-coordinate position
    color: string;      // circle color from colors array
    speed: number;      // movement speed
    blur: number;       // blur effect intensity
    direction: number;  // -1 for left, 1 for right
    opacity: number;    // transparency value
    zIndex: number;     // layering order
}

const FRAME_DELAY = 33; 

class CircleAnimation {
    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
    circles: Circle[] = [];
    // Color palette for circles
    colors: string[] = ['#d36c29', '#22193a', '#9f9e9d', '#b7ab97', '#c7c5c5'];
    circleSize: number = 70;        // Base radius of each circle
    minSpeed: number = 0.5;         // Minimum movement speed
    maxSpeed: number = 2;           // Maximum movement speed
    minBlur: number = 0;            // Minimum blur effect
    maxBlur: number = 5;            // Maximum blur effect
    newCircleInterval: number = 1000; // Time between new circle generations (in ms)
    initialCircleCount: number = 0;      // Initial number of circles based on screen width
    blurPadding: number = 10;        // Adjust based on maxBlur value
    isResizing: boolean = false;     // Flag to track resize state
    resizeTimeout: number | null = null; // For resize debounce
    container: HTMLElement;

    lastFrameTime: number = 0; // Store the last frame timestamp

    constructor(container: HTMLElement) {
        this.container = container;
        this.initialize();
        // Handle window resizing
        window.addEventListener('resize', this.handleResize);
    }

    // Initialize the animation
    initialize() {
        // Setup canvas
        this.canvas = document.createElement('canvas');
        this.ctx = this.canvas.getContext('2d') as CanvasRenderingContext2D;
        
        // Clear any existing canvas
        while (this.container.firstChild) {
            this.container.removeChild(this.container.firstChild);
        }
        
        this.container.appendChild(this.canvas);
        this.resizeCanvas();
        
        // Calculate initial circle count
        this.initialCircleCount = Math.floor(window.innerWidth / 100);
        
        // Initialize circles
        this.circles = [];
        this.initCircles();
        
        // Start animation
        this.animate();
    }

    // Handle window resize
    handleResize = () => {
        if (this.resizeTimeout) {
            window.clearTimeout(this.resizeTimeout);
        }
        
        this.resizeTimeout = window.setTimeout(() => {
            this.initialize(); // Reinitialize everything
            this.resizeTimeout = null;
        }, 150);
    }

    // Adjust canvas size when window is resized
    resizeCanvas() {
        const dpr = window.devicePixelRatio || 1;

        // Set the canvas dimensions
        this.canvas.width = this.container.clientWidth * dpr;
        this.canvas.height = (this.container.clientHeight + (this.blurPadding * 2)) * dpr;

        this.ctx.scale(dpr, dpr);
        
        // Set style properties to prevent unwanted growth
        this.canvas.style.width = `${this.container.clientWidth}px`;
        this.canvas.style.height = `${this.container.clientHeight}px`;
        this.canvas.style.marginTop = `-${this.blurPadding}px`;
        this.canvas.style.display = 'block';
        this.canvas.style.position = 'relative';
        this.canvas.style.overflow = 'hidden';
    }

    // Initialize the first set of circles
    initCircles() {
        const spacing = this.circleSize * 2;
        const rowY = this.circleSize + this.blurPadding;
        const circlesPerRow = Math.ceil(this.canvas.width / spacing);
        
        // Create initial circles
        for (let i = 0; i < circlesPerRow; i++) {
            const direction = Math.random() < 0.5 ? -1 : 1;
            const x = Math.random() * this.canvas.width;
            const color = this.colors[Math.floor(Math.random() * this.colors.length)];
            const speed = Math.random() * (this.maxSpeed - this.minSpeed) + this.minSpeed;
            const blur = Math.random() * (this.maxBlur - this.minBlur) + this.minBlur;
            const opacity = Math.random() * 0.2 + 0.7;
            const zIndex = Math.floor(Math.random() * 10);
            
            this.circles.push({ 
                x, 
                y: rowY, 
                color, 
                speed, 
                blur, 
                direction, 
                opacity, 
                zIndex 
            });
        }
    }

    // Create a new circle with optional forced y-position for row balancing
    createCircle(forcedY?: number) {
        const direction = Math.random() < 0.5 ? -1 : 1;
        const x = direction === 1 ? -this.circleSize : this.canvas.width / (window.devicePixelRatio || 1) + this.circleSize;
        const y = this.circleSize + this.blurPadding;  // Single row position
        
        const color = this.colors[Math.floor(Math.random() * this.colors.length)];
        const speed = Math.random() * (this.maxSpeed - this.minSpeed) + this.minSpeed;
        const blur = Math.random() * (this.maxBlur - this.minBlur) + this.minBlur;
        const opacity = Math.random() * 0.2 + 0.7;
        const zIndex = Math.floor(Math.random() * 10);

        this.circles.push({ x, y, color, speed, blur, direction, opacity, zIndex });
    }

    // Main animation loop
    animate() {
        const now = performance.now();
        
        if (now - this.lastFrameTime >= FRAME_DELAY) {
            this.lastFrameTime = now;
            
            this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
            
            // Only create new circles if not resizing
            if (!this.isResizing && Math.random() < 1 / (this.newCircleInterval / 16)) {
                this.createCircle();
            }

            const sortedCircles = [...this.circles].sort((a, b) => a.zIndex - b.zIndex);

            for (let i = 0; i < sortedCircles.length; i++) {
                const circle = sortedCircles[i];

                this.ctx.filter = `blur(${circle.blur}px)`;
                this.ctx.globalAlpha = circle.opacity;
                this.ctx.fillStyle = circle.color;
                this.ctx.beginPath();
                this.ctx.arc(circle.x, circle.y, this.circleSize, 0, Math.PI * 2);
                this.ctx.fill();

                circle.x += circle.speed * circle.direction;

                if (circle.x < -this.circleSize || circle.x > this.canvas.width + this.circleSize) {
                    circle.direction = 0;
                }
            }

            this.circles = this.circles.filter(circle => circle.direction !== 0);
            this.ctx.globalAlpha = 1;
        }

        setTimeout(() => requestAnimationFrame(() => this.animate()), FRAME_DELAY);
    }
}

// Initialize animation when page loads
window.addEventListener('load', () => {
    const container = document.getElementById('dots');
    if (container) {
        new CircleAnimation(container);
    }
});